import { apiClient } from '@indipia/shared';

// hermes NotificationItemResponse 정합
export interface NotifItem {
  id: number;
  groupId: number | null;
  sourceAgent: string;
  notiType: string;
  /** 표시 카테고리(ZEU_NOTI_CATEGORY 코드). 과거 알림은 서버가 STUDIO로 내려준다 */
  notiCategory?: string | null;
  title: string;
  body: string;
  read: boolean;
  readDtm: string | null;
  targetType: string | null;
  targetId: number | null;
  siteId: number | null;
  linkUrl: string | null;
  /** 클릭 시 이동할 웹 화면/메뉴 코드 (TM_MENU.MENU_CODE). 없으면 null(라우팅 없음) */
  webMenuCode: string | null;
  /** 클릭 시 이동할 앱 화면/메뉴 코드. webMenuCode가 null인데 이 값만 있으면 "앱 전용 화면" 안내 대상 */
  appMenuCode: string | null;
  createdAt: string;
}

// GET /hermes/notifications — Spring Page 응답에서 content 추출
export async function fetchNotifications(memberId: number, category?: string): Promise<NotifItem[]> {
  const res = await apiClient.get('/hermes/notifications', {
    params: { memberId, size: 100, ...(category ? { category } : {}) },
  });
  return (res.data?.data?.content ?? res.data?.content ?? []) as NotifItem[];
}

/**
 * 스튜디오 홈 현장카드 "최근N일 중요 알림" 대상 notiType — 고객·반장님이 공유받은 자료를 확인/응답한 알림,
 * 일정확인 회신 결과 알림(일정 확정 / 불가 회신 / 다음 순위 자동 발송 / 미확정) + 착공 임박 확정 처리.
 * EXTERNAL_URL_RESPONDED 는 일정확인 회신 알림이 CREW_CONFIRM_* 로 바뀌기 전 발송분 조회용으로 유지.
 */
export const IMPORTANT_NOTI_TYPES = [
  'EXTERNAL_URL_VIEWED',
  'EXTERNAL_URL_RESPONDED',
  'CREW_CONFIRM_ACCEPTED',
  'CREW_CONFIRM_FORWARDED',
  'CREW_CONFIRM_UNFIXED',
  'CREW_CONFIRM_DECLINED',
  'CREW_CONFIRM_FIXED',
] as const;

/**
 * 스튜디오 홈 현장카드 "최근N일 중요 알림" 배치 조회.
 * siteId 다건 — 여러 현장을 한 번에 조회하고, 응답 각 항목의 siteId로 FE가 현장별로 그룹핑한다
 * (스튜디오홈_현장카드_플랫폼공통API_요청.md §3).
 */
export async function fetchImportantNotifications(
  memberId: number,
  siteIds: number[],
  sinceDays = 2,
): Promise<NotifItem[]> {
  if (siteIds.length === 0) return [];
  const res = await apiClient.get('/hermes/notifications', {
    params: {
      memberId,
      siteId: siteIds.join(','),
      notiType: IMPORTANT_NOTI_TYPES.join(','),
      sinceDays,
      size: 100,
    },
  });
  return (res.data?.data?.content ?? res.data?.content ?? []) as NotifItem[];
}

// PATCH /hermes/notifications/{id}/read
export async function markOneRead(id: number): Promise<void> {
  await apiClient.patch(`/hermes/notifications/${id}/read`);
}

// PATCH /hermes/notifications/read-all — category 미지정 시 전체, 지정 시 해당 표시 카테고리만
export async function markAllRead(memberId: number, category?: string): Promise<number> {
  const res = await apiClient.patch('/hermes/notifications/read-all', undefined, {
    params: { memberId, ...(category ? { category } : {}) },
  });
  return (res.data?.data?.updatedCount ?? 0) as number;
}
